<template src="./index.html"></template>

<script>
import FlagButton from "@/components/FlagButton.vue";
import BeFooter from "@/components/BeFooter.vue";
import Recaptcha from "@/components/Recaptcha.vue";
import { S3Service } from "@/services/s3.service.js";
import axios from "axios";
import { lookup } from 'mime-types'
import { required, email } from "vuelidate/lib/validators";

// Idiomas de CONTEÚDO do formulário (os mesmos 4 do FlagButton). Idioma fora da
// lista cai em pt-BR: evita máscara de data, bandeira destacada e `language` do
// POST divergindo do idioma realmente renderizado.
const SUPPORTED_LANGUAGES = ["pt-BR", "en", "esp", "de"];

// Onde cada campo PADRÃO mora no state, para gerar as regras do vuelidate e ler
// o validador correspondente. Não é uniforme: a data de nascimento fica na raiz
// (é a string com máscara, não o valor já convertido em data.birth_date) e os
// anexos ficam nos arrays de validFiles.
const BUILT_IN_MODEL_PATHS = {
  name: "data.name",
  cpf: "data.cpf",
  birth_date: "birth_date",
  email: "data.email",
  phone: "data.phone",
  message: "data.message",
  location: "data.location",
  type: "data.type",
  holder_occupation: "data.holder_occupation",
  applicant: "data.applicant",
  files: "validFiles.defaultFiles",
  applicant_files: "validFiles.applicantFiles",
};

// Chaves com que o 422 volta para cada campo PADRÃO. Quase sempre é a própria
// `key` do manifesto, mas os dois anexos fogem da regra: o backend responde pelo
// nome do payload / da flag legada (`applicantFiles`, `file_field`) em vez de
// `applicant_files` / `files`, então o destaque vermelho nunca chegava neles.
// Aceita as duas formas para não depender de qual lado muda primeiro.
const BUILT_IN_ERROR_KEYS = {
  files: ["files", "file_field"],
  applicant_files: ["applicant_files", "applicantFiles"],
};

// Escreve `value` em `target` seguindo os segmentos ["a","b","c"], criando os nós.
// Recebe os MESMOS segmentos que `fieldModelSegments` devolve, para que a regra
// do vuelidate e a leitura do validador nunca possam divergir de caminho.
function setByPath(target, segments, value) {
  const path = [...segments];
  const leaf = path.pop();
  const parent = path.reduce((node, segment) => {
    if (!node[segment]) node[segment] = {};
    return node[segment];
  }, target);
  parent[leaf] = value;
}

// Lê `target` seguindo um caminho já quebrado em segmentos, sem estourar no meio.
function getByPath(target, segments) {
  return segments.reduce(
    (node, segment) => (node ? node[segment] : undefined),
    target,
  );
}

export default {
  name: "FormPage",
  components: { FlagButton, BeFooter, Recaptcha },

  props: {
    msg: String,
    client: String,
    form: String,
  },

  data() {
    return {
      localLanguage: "pt-BR",
      holderOccupations: [],
      saving: false,
      showForm: true,
      showError: false,
      options: null,
      error_load: false,
      error_unavaliable: false,
      tab: "new",
      errors: {},
      types: [],
      files: [],
      applicantFiles: [],
      validFiles: { defaultFiles: [], applicantFiles: []},
      invalidFiles: { defaultFiles: [], applicantFiles: []},
      customFiles: {},
      // Anexos custom recusados por tamanho. Ficam à mostra (como os padrão em
      // invalidFiles) para o titular poder removê-los e liberar o Enviar.
      invalidCustomFiles: {},
      openMultiKey: null,
      // Arquivo recusado por tamanho. Escrita só por refreshFileSizeError, e é a
      // ÚNICA mensagem que desabilita o Enviar: a seleção atual não tem como ser
      // enviada até o titular remover o arquivo.
      errorFileMessage: "",
      // Erro do envio (upload falhou, evidência faltando). Separada da de tamanho
      // porque aqui o titular precisa poder tentar de novo — mensagem que
      // desabilita o botão deixava o formulário num beco sem saída.
      submitErrorMessage: "",
      maxFileSize: 10 * 1024 * 1024,
      fields: [],
      data: {
        name: "",
        custom_fields: {},
      },
      showStatus: false,
      showErrorQuery: false,
      protocol: "",
      errorsQuery: {},
      statusText: "",
      site_url: process.env.VUE_APP_SITE_URL,
      cpf_required: false,
      privacyPolicyUrl: null,
      locations: [],
      birth_date: null,
      recaptchaToken: null,
      otherOccupationId: null,
    };
  },

  mounted() {
    document.addEventListener("click", this.onDocCloseMulti);
    if (!this.client) {
      this.client = this.$route.query.client;
    }
    if (!this.form) {
      this.form = this.$route.query.form;
    }

    const { query } = this.$route;
    let { language = "pt-BR" } = query;
    if ("lang" in query) {
      language = query.lang;
    }
    if (language == "es") {
      language = "esp";
    }
    language = String(language).trim();
    switch (language) {
      case "es":
        language = "esp";
        break;
      case "pt":
        language = "pt-BR";
        break;
    }
    if (!SUPPORTED_LANGUAGES.includes(language)) {
      language = "pt-BR";
    }
    this.$i18n.locale = language;
    // Deep link `?lang=<idioma>`: sem isto o localLanguage continuava "pt-BR" e
    // a bandeira destacada, a conversão da data e o `language` enviado no POST
    // ficavam no idioma errado até o titular clicar numa bandeira.
    this.localLanguage = language;

    this.validateCompany().then((res) => {
      if (res) {
        this.createForm({ language })
      } else {
        this.error_unavaliable = true
      }
    });
  },

  beforeDestroy() {
    document.removeEventListener("click", this.onDocCloseMulti);
  },
  computed: {
    // Bypass do reCAPTCHA APENAS em execução local (yarn serve): o site key não
    // casa com localhost e o widget não renderiza, travando o botão Enviar pra
    // sempre.
    //
    // Duas travas: build de produção nunca entra aqui (o ramo é eliminado no
    // bundle) e, mesmo em dev, só vale em localhost. O gate do front nunca foi
    // a fronteira de segurança — quem valida o token é o backend.
    isLocalEnv() {
      return (
        process.env.NODE_ENV !== "production" &&
        ["localhost", "127.0.0.1"].includes(window.location.hostname)
      );
    },
    computedChipColors() {
      const colors = {
        bgc: "#17a2b8",
        fc: "#ffffff",
      };

      const bg_hex =
        typeof this.options.background_color === "string"
          ? this.options.background_color
          : this.options.background_color?.hex;
      const fc_hex =
        typeof this.options.font_color === "string"
          ? this.options.font_color
          : this.options.font_color?.hex;

      if (bg_hex) {
        if (this.getContrastRatio("#ffffff", bg_hex) >= 3) {
          colors.bgc = bg_hex;
          colors.fc = fc_hex ? fc_hex : "#ffffff";
        } else {
          if (fc_hex) {
            colors.bgc = fc_hex;
            colors.fc = bg_hex;
          }
        }
      } else if (fc_hex) {
        if (this.getContrastRatio("#ffffff", fc_hex) >= 3) {
          colors.bgc = fc_hex;
        }
      }

      return colors;
    },

    // Opções do Solicitante: os rótulos vêm do i18n, então precisam ser computed.
    // Montadas no `data`, congelavam no idioma da abertura da página e o select
    // continuava em português depois de trocar a bandeira — os outros catálogos
    // (tipos, locais, ocupações) não têm o problema porque o createForm os
    // recarrega traduzidos a cada troca.
    applicantOptions() {
      return [
        { value: "holder", text: this.$t("form.applicantOptions.holder") },
        {
          value: "legal_representative",
          text: this.$t("form.applicantOptions.legalRepresentative"),
        },
      ];
    },

    // "10 MB" para a mensagem de arquivo grande, derivado do próprio limite.
    maxFileSizeLabel() {
      return `${Math.round(this.maxFileSize / (1024 * 1024))} MB`;
    },

    // Campos a renderizar (§2.1), ordenados por `order` — os que estão ATIVOS
    // (visíveis, com a condição satisfeita e com o gatilho também ativo).
    //
    // Nada de exceção para quem tem dependent_of: a Evidência vem do backend
    // como visible:true (HolderFormFieldsService) e é o Solicitante que nasce
    // visible:false, então a condição já basta para escondê-la. Abrir exceção
    // fazia o olho de ocultar do editor ser ignorado em campo condicional.
    visibleFields() {
      return this.fields
        .filter((field) => this.isFieldActive(field))
        .slice()
        .sort((a, b) => (a.order || 0) - (b.order || 0));
    },

    // field_keys de TODOS os campos com input visíveis — base do clear-on-hide
    // (watch). Antes só os customs entravam, então um campo PADRÃO condicional
    // ocultado continuava indo no payload pela chave legada em `data.*`.
    visibleFieldKeys() {
      return this.visibleFields
        .filter((field) => field.type !== "support")
        .map((field) => field.field_key);
    },
  },

  watch: {
    // Ao ocultar um campo condicional, limpa seu valor (§2.2 / contrato): campo
    // inativo não é exigido nem persistido pelo backend.
    visibleFieldKeys(next, previous) {
      previous.forEach((key) => {
        if (!next.includes(key)) this.clearFieldValue(key);
      });
    },
  },

  methods: {
    // Abre o seletor de arquivo do container clicado. Funciona dentro do v-for
    // (sem refs, que viram array no loop): acha o <input type=file> do próprio
    // container. Ignora cliques em botões (chips/remover/limpar).
    openFilePicker(event) {
      const target = event.target;
      if (target && target.closest("button")) return;
      // Clique direto no <input> já abre nativamente — não redisparar.
      if (target && target.matches && target.matches('input[type="file"]')) return;
      const input = event.currentTarget.querySelector('input[type="file"]');
      if (input) input.click();
    },
    handleFiles(event, field = 'defaultFiles') {
      this.deleteAllFiles(field);
      const selectedFiles = Array.from(event.target.files);
      const validFiles = [];
      const invalidFiles = [];

      selectedFiles.forEach((file) => {
        if (file.size <= this.maxFileSize) {
          validFiles.push(file);
        } else {
          invalidFiles.push(file);
        }
      });

      if (field == "applicantFiles") {
        this.applicantFiles = selectedFiles
      } else {
        this.files = selectedFiles;
      }

      this.validFiles[field] = validFiles;
      this.invalidFiles[field] = invalidFiles;
      this.refreshFileSizeError();
      event.target.value = '';
    },
    deleteFile(index, type, field) {
      if (type === "valid") {
        this.validFiles[field].splice(index, 1);
      } else if (type === "invalid") {
        this.invalidFiles[field].splice(index, 1);
        this.refreshFileSizeError();
      }
    },
    deleteAllFiles(field) {
      if (!field) {
        this.validFiles = { defaultFiles: [], applicantFiles: []};
        this.invalidFiles = { defaultFiles: [], applicantFiles: []};
        this.refreshFileSizeError();
        return
      }

      this.validFiles[field] = []
      this.invalidFiles[field] = []
      this.refreshFileSizeError();
    },
    // Refaz a mensagem de arquivo grande a partir de TODAS as seleções recusadas
    // (padrão e personalizadas). Centralizada porque a mensagem também desabilita
    // o Enviar: montada dentro de cada handler, sobrava mensagem sem nenhum
    // arquivo recusado na tela — e o rebuild antigo imprimia "[object File]" por
    // dar join no File em vez do nome.
    refreshFileSizeError() {
      const rejected = [];
      Object.keys(this.invalidFiles).forEach((key) => {
        rejected.push(...(this.invalidFiles[key] || []));
      });
      Object.keys(this.invalidCustomFiles).forEach((key) => {
        rejected.push(...(this.invalidCustomFiles[key] || []));
      });
      this.errorFileMessage = rejected.length
        ? this.$t("errors.fileSizeError", {
            limit: this.maxFileSizeLabel,
            files: rejected.map((file) => file.name).join(", "),
          })
        : "";
    },
    getLang2Letters() {
      return this.$i18n.locale.substring(0, 2);
    },
    // O FlagButton já trocou o $i18n.locale antes de emitir. Como a ordem da
    // máscara de data muda com o idioma (pt-BR/esp = dd/mm/aaaa, en/de =
    // aaaa/mm/dd), reescreve as datas já digitadas no novo formato — lendo-as
    // com o idioma ANTERIOR, que ainda está em localLanguage.
    handleFlagChange(language) {
      const typedDates = this.collectTypedDatesAsIso(this.localLanguage);
      this.localLanguage = language;
      this.applyTypedDatesFromIso(typedDates, language);
      this.createForm({ language });
      this.$emit("changeLanguage", language);
    },
    handleHolderOccupationChange(value) {
      if (value !== "other") {
        this.data.holder_occupation_description = "";
      }
    },
    clearForm() {
      this.data = { custom_fields: {} };
      // Antes do initCustomFields, que reidrata as chaves de customFiles.
      this.customFiles = {};
      this.invalidCustomFiles = {};
      this.initCustomFields();
      this.birth_date = null;
      this.showForm = true;
      this.showError = false;
      this.submitErrorMessage = "";
      this.errors = {};
      this.recaptchaToken = null;
      this.deleteAllFiles();
      this.$v.$reset();
    },
    async validateCompany() {
      try {
         await axios
        .get(process.env.VUE_APP_API_URL + `api/becompliance/company_uuid`, {
          params: {
            company_uuid: this.client,
          },
        })
        return true
      } catch (error) {
        return false
      }
    },
    async createForm(params = { language: "pt-BR" }) {
      await axios
        .get(
          process.env.VUE_APP_CLOUD_FRONT_URL +
            `${this.client}/${this.form}${params.language}.json`
        )
        .then((res) => {
          if (!res.data.options) throw Error();
          this.locations = res.data.locations;
          this.holderOccupations = res.data.holder_occupations;
          const occupations = res.data.holder_occupations;
          const otherOccupations = occupations.filter((item) => {
            const isOtherOccupation =
              item.text === "Outro (opção para descrever)" ||
              item.text === "Otro (Opción para describir)" ||
              item.text === "Other (Option to describe)" ||
              item.text === "Outro (opção para descrever)" ||
              item.text === "Sonstiges (Option zur Beschreibung)";
            return isOtherOccupation;
          });
          this.otherOccupationId = otherOccupations.at(0)?.value;
          this.holderOccupations = res.data.holder_occupations;
          this.holderOccupations = this.holderOccupations.filter(
            (item) => item.value !== this.otherOccupationId
          );
          // A opção "Outro" não vem na resposta quando está desativada; sem a
          // guarda, o push insere undefined na lista e o select quebra o render.
          if (otherOccupations.length) {
            this.holderOccupations.push(otherOccupations.at(0));
          }
          this.options = { ...res.data.options };
          this.fields = (res.data.options && res.data.options.fields) || [];
          this.initCustomFields();
          this.types = res.data.types;
          this.cpf_required = res.data.cpf_required;
          this.privacyPolicyUrl = this.options.privacy_policy_url;
          this.$emit("getform", res.data);
        })
        .catch((err) => {
          axios
            .get(process.env.VUE_APP_API_URL + "lgpd-titular/form", {
              params: {
                client: this.client,
                form: this.form,
                ...params,
              },
            })
            .then((res) => {
              this.locations = res.data.locations;
              const occupations = res.data.holder_occupations;
              const otherOccupations = occupations.filter((item) => {
                const isOtherOccupation =
                  item.text === "Outro (opção para descrever)" ||
                  item.text === "Otro (Opción para describir)" ||
                  item.text === "Other (Option to describe)" ||
                  item.text === "Outro (opção para descrever)" ||
                  item.text === "Sonstiges (Option zur Beschreibung)";
                return isOtherOccupation;
              });
              this.otherOccupationId = otherOccupations.at(0)?.value;
              this.holderOccupations = res.data.holder_occupations;
              this.holderOccupations = this.holderOccupations.filter(
                (item) => item.value !== this.otherOccupationId
              );
              if (otherOccupations.length) {
                this.holderOccupations.push(otherOccupations.at(0));
              }
              this.options = { ...res.data.options };
              this.fields = (res.data.options && res.data.options.fields) || [];
              this.initCustomFields();
              this.types = res.data.types;
              this.cpf_required = res.data.cpf_required;
              this.privacyPolicyUrl = this.options.privacy_policy_url;
              this.$emit("getform", res.data);
            })
            .catch((err) => {
              this.error_load = true;
              this.$emit("getform", null);
            });
        });
    },

    queryForm() {
      if (!this.protocol || this.protocol.trim().length == 0) {
        return;
      }
      this.saving = true;
      this.showErrorQuery = false;
      this.errorsQuery = {};
      this.showStatus = false;
      this.statusText = "";

      let url = process.env.VUE_APP_API_URL;
      url += "lgpd-titular/status";
      url += `/${this.client}/${this.protocol}`;

      axios
        .get(url)
        .then((res) => {
          this.showStatus = true;
          this.statusText = res.data.status;
        })
        .catch(() => {
          this.showStatus = true;
          this.statusText = "Protocolo não encotrado";
        })
        .finally(() => {
          this.saving = false;
        });
    },

    // Valor default de um campo por tipo (§2.2): multi/file = array; senão string.
    defaultForType(type) {
      return type === "multi" || type === "file" ? [] : "";
    },

    // Inicializa data.custom_fields com uma entrada por campo custom (§2.2),
    // no valor default do tipo — mantém a reatividade do Vue nas chaves.
    // Valor já digitado num campo que continua existindo é PRESERVADO: trocar a
    // bandeira recarrega o formulário (createForm) e sem isto tudo que o titular
    // tinha preenchido nos campos personalizados era descartado, enquanto os
    // campos padrão (data.*) sobreviviam.
    initCustomFields() {
      const previous = this.data.custom_fields || {};
      const customFields = {};
      this.fields.forEach((field) => {
        if (field.category !== "custom" || field.type === "support") return;
        const fallback = this.defaultForType(field.type);
        const hadValue = Object.prototype.hasOwnProperty.call(
          previous,
          field.field_key,
        );
        // Só reaproveita se o formato bater (o campo pode ter trocado de tipo
        // no editor entre duas cargas do formulário).
        const sameShape =
          Array.isArray(previous[field.field_key]) === Array.isArray(fallback);
        customFields[field.field_key] =
          hadValue && sameShape ? previous[field.field_key] : fallback;
      });
      this.$set(this.data, "custom_fields", customFields);

      // Anexos custom moram em `customFiles` (é o que o vuelidate observa — ver
      // fieldModelSegments). Garante a chave por campo, preservando o que o
      // titular já selecionou, para a regra de obrigatório reagir à seleção.
      const previousFiles = this.customFiles || {};
      const previousInvalidFiles = this.invalidCustomFiles || {};
      const nextFiles = {};
      const nextInvalidFiles = {};
      this.fields.forEach((field) => {
        if (field.category !== "custom" || field.type !== "file") return;
        nextFiles[field.field_key] = previousFiles[field.field_key] || [];
        nextInvalidFiles[field.field_key] =
          previousInvalidFiles[field.field_key] || [];
      });
      this.customFiles = nextFiles;
      this.invalidCustomFiles = nextInvalidFiles;
      // Campo que deixou de existir na nova carga leva a recusa dele embora.
      this.refreshFileSizeError();
    },

    // Texto de um objeto i18n no idioma corrente, SEM fallback (decisão da dev):
    // idioma sem tradução → vazio, igual ao form real. Usado pelo template (#14216).
    localizedText(value) {
      if (!value) return "";
      if (typeof value === "string") return value;
      return value[this.$i18n.locale] || "";
    },

    // `key` do campo PADRÃO, para o template escolher o input built-in; null em
    // campo personalizado. O despacho não pode ser só por `key`: o key de um
    // campo custom é o slug do rótulo pt-BR, então um campo rotulado "Email" ou
    // "Type" colidiria com o built-in e seria bindado em data.email/data.type
    // em vez de data.custom_fields[field_key].
    builtInKey(field) {
      if (!field || field.category === "custom") return null;
      return field.key || null;
    },

    // Valor atual de um campo: custom vem de data.custom_fields[field_key];
    // built-in vem da chave legada em data (name/type/holder_occupation/...).
    fieldValue(field) {
      if (!field) return undefined;
      if (field.category === "custom") {
        return this.data.custom_fields
          ? this.data.custom_fields[field.field_key]
          : undefined;
      }
      return this.data[field.key];
    },

    // Um campo está ATIVO quando está visível, a condição dele está satisfeita
    // (dependent_of avaliado string-agnostic e multi-resposta) E o gatilho também
    // está ativo. A RECURSÃO importa: o backend avalia a cadeia inteira (ms, voz
    // e editor idem), então sem ela uma cadeia A→B→C deixava C na tela mesmo com
    // A oculto — o front exigia campo que o servidor descarta.
    //
    // Gatilho ausente do formulário → não esconde (mesma regra do backend).
    // `seen` protege contra ciclo (A→B→A): trata como não satisfeito em vez de
    // estourar a pilha.
    isFieldActive(field, seen = []) {
      if (!field) return false;
      if (field.visible === false) return false;
      const dep = field.dependent_of;
      if (!dep || !dep.field_key) return true;
      if (seen.includes(field.field_key)) return false;
      const trigger = this.fields.find((f) => f.field_key === dep.field_key);
      if (!trigger) return true;
      if (!this.isFieldActive(trigger, seen.concat([field.field_key]))) {
        return false;
      }
      const value = this.fieldValue(trigger);
      const answers = Array.isArray(value) ? value : [value];
      const expected = (dep.values || []).map((v) => String(v));
      return answers.some((answer) => expected.includes(String(answer)));
    },

    // Zera o valor de um campo ao ele ser ocultado. Cobre os DOIS lados: custom
    // (valor em data.custom_fields + seleção de anexo em customFiles) e PADRÃO
    // (chave legada em data.*, birth_date na raiz, anexos em validFiles).
    clearFieldValue(fieldKey) {
      const field = this.fields.find((f) => f.field_key === fieldKey);
      if (!field) return;

      if (field.category === "custom") {
        this.$set(
          this.data.custom_fields,
          fieldKey,
          this.defaultForType(field.type),
        );
        // A seleção do anexo custom mora em customFiles — é o que o vuelidate
        // observa e o que o postForm sobe.
        if (field.type === "file") {
          this.$set(this.customFiles, fieldKey, []);
          this.$set(this.invalidCustomFiles, fieldKey, []);
          this.refreshFileSizeError();
        }
        return;
      }

      switch (field.key) {
        case "birth_date":
          // Mora na raiz (string com máscara), não em data.birth_date.
          this.birth_date = null;
          return;
        case "files":
          this.files = [];
          this.deleteAllFiles("defaultFiles");
          return;
        case "applicant_files":
          this.applicantFiles = [];
          this.deleteAllFiles("applicantFiles");
          return;
        case "holder_occupation":
          // A descrição do "Outro" só existe por causa da ocupação.
          this.$set(this.data, "holder_occupation_description", "");
          break;
      }

      if (
        Object.prototype.hasOwnProperty.call(BUILT_IN_MODEL_PATHS, field.key)
      ) {
        this.$set(this.data, field.key, "");
      }
    },

    // "Vazio" numa única definição: usada pela opção "limpar seleção" do select e
    // pela omissão de chave no payload.
    isEmptyValue(value) {
      if (Array.isArray(value)) return value.length === 0;
      return value === "" || value === null || value === undefined;
    },

    hasFieldValue(field) {
      return !this.isEmptyValue(this.fieldValue(field));
    },

    // Campo PADRÃO ativo por `key` (undefined se oculto/condição não satisfeita).
    // É por aqui que o postForm consulta o manifesto no lugar das flags legadas.
    activeFieldByKey(key) {
      return this.visibleFields.find((field) => field.key === key);
    },

    // A máscara de data vem do i18n e a ORDEM dos componentes muda por idioma:
    // pt-BR/esp = `##/##/####` (dia primeiro), en/de = `####/##/##` (ano
    // primeiro). Toda conversão de data passa por aqui em vez de assumir
    // dd/mm/aaaa. `language` omitido = idioma corrente.
    dateMaskStartsWithYear(language) {
      const mask = language
        ? this.$t("form.mask.birth_date", language)
        : this.$t("form.mask.birth_date");
      return String(mask).indexOf("####") === 0;
    },

    // Data digitada sob a máscara do idioma → AAAA-MM-DD (contrato §2.2).
    // Valor vazio/incompleto é mantido como está (o backend valida).
    maskedDateToIso(value, language) {
      if (!value || typeof value !== "string") return value;
      const parts = value.trim().split("/");
      if (parts.length !== 3 || parts.some((part) => !part)) return value;
      const [year, month, day] = this.dateMaskStartsWithYear(language)
        ? parts
        : [parts[2], parts[1], parts[0]];
      return `${year}-${month}-${day}`;
    },

    // Inverso de maskedDateToIso: AAAA-MM-DD → formato da máscara do idioma.
    isoToMaskedDate(value, language) {
      if (!value || typeof value !== "string") return value;
      const parts = value.split("-");
      if (parts.length !== 3 || parts.some((part) => !part)) return value;
      const [year, month, day] = parts;
      return this.dateMaskStartsWithYear(language)
        ? `${year}/${month}/${day}`
        : `${day}/${month}/${year}`;
    },

    // A data digitada existe no calendário? A máscara só garante dígitos, então
    // `31/02/2026` passava e ia para o payload como "2026-02-31". Campo vazio é
    // válido aqui — quem exige resposta é a regra de `required`.
    //
    // Independe de idioma e de fuso: a ordem dos componentes vem da máscara do
    // idioma (maskedDateToIso) e a checagem é sobre os números, sem nenhuma
    // conversão de timezone envolvida.
    isValidMaskedDate(value, language) {
      if (!value) return true;
      return this.isValidIsoDate(this.maskedDateToIso(value, language));
    },

    // AAAA-MM-DD que existe de fato (recusa mês 13, dia 0 e 31/02, respeitando ano
    // bissexto). Compara componente a componente porque o Date do JS "conserta"
    // data inválida sozinho — `new Date("2026-02-31")` vira 03/03 em vez de falhar.
    isValidIsoDate(value) {
      if (typeof value !== "string") return false;
      const parts = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
      if (!parts) return false;
      const year = Number(parts[1]);
      const month = Number(parts[2]);
      const day = Number(parts[3]);
      if (month < 1 || month > 12 || day < 1) return false;
      // Dia 0 do mês seguinte = último dia do mês procurado. Em UTC para o fuso
      // local não deslocar a virada do mês.
      const lastDayOfMonth = new Date(Date.UTC(year, month, 0)).getUTCDate();
      return day <= lastDayOfMonth;
    },

    // Datas já digitadas (built-in birth_date + campos custom type=date) em ISO,
    // lidas com a máscara de `language`. Chave "birth_date" = campo built-in.
    collectTypedDatesAsIso(language) {
      const typed = {
        birth_date: this.maskedDateToIso(this.birth_date, language),
      };
      this.fields.forEach((field) => {
        if (field.category !== "custom" || field.type !== "date") return;
        typed[field.field_key] = this.maskedDateToIso(
          this.data.custom_fields ? this.data.custom_fields[field.field_key] : "",
          language,
        );
      });
      return typed;
    },

    // Reescreve as datas coletadas na máscara de `language`. Campo que não tinha
    // data completa fica intocado (não sobrescreve o que o titular digitou).
    applyTypedDatesFromIso(typedDates, language) {
      Object.keys(typedDates).forEach((key) => {
        const iso = typedDates[key];
        if (!iso || typeof iso !== "string") return;
        const masked = this.isoToMaskedDate(iso, language);
        if (key === "birth_date") {
          this.birth_date = masked;
        } else if (this.data.custom_fields) {
          this.$set(this.data.custom_fields, key, masked);
        }
      });
    },

    // Monta o custom_fields a enviar (§2.2): só campos custom ATIVOS; support não
    // entra; file/multi já são arrays.
    //
    // Campo opcional sem resposta é OMITIDO em vez de ir como "": o backend valida
    // o valor contra os ids das opções e "" não é opção nenhuma, então um select
    // opcional deixado em branco tomava 422 ("valor fora das opções"). Chave
    // ausente = sem resposta, que é exatamente o que a opção "limpar seleção"
    // significa. Campo obrigatório vazio nunca chega aqui (o vuelidate barra).
    buildCustomFieldsPayload() {
      const payload = {};
      this.visibleFields.forEach((field) => {
        if (field.category !== "custom" || field.type === "support") return;
        let value = this.data.custom_fields[field.field_key];
        if (field.type === "date") value = this.maskedDateToIso(value);
        if (this.isEmptyValue(value)) return;
        payload[field.field_key] = value;
      });
      return payload;
    },

    // Regras vuelidate de um field: required (array não-vazio para multi/file),
    // formato de e-mail e data existente. Vale para campo padrão e personalizado
    // (o `birth_date` também é `type: date` no manifesto).
    fieldRules(field) {
      const rules = {};
      if (field.required) {
        rules.required =
          field.type === "multi" || field.type === "file"
            ? (value) => Array.isArray(value) && value.length > 0
            : required;
      }
      if (field.type === "email") rules.email = email;
      // Converter é uma coisa, validar é outra: `maskedDateToIso` segue sendo só
      // formatação, e a existência da data é regra do formulário — assim o titular
      // vê o campo em vermelho na hora, em vez de descobrir no 422.
      if (field.type === "date") {
        rules.validDate = (value) => this.isValidMaskedDate(value);
      }
      return rules;
    },

    // Caminho do valor de um field no state — fonte única, usada para montar as
    // regras do vuelidate (validations) E para ler o validador (fieldError).
    //
    // Anexo custom valida a SELEÇÃO (`customFiles`), não `data.custom_fields`:
    // este último só recebe os uuids depois do upload, que acontece DEPOIS do
    // gate de validação no postForm — validá-lo deixava todo campo de anexo
    // obrigatório permanentemente inválido. Mesma escolha do anexo padrão, que
    // aponta para `validFiles.defaultFiles`.
    fieldModelSegments(field) {
      if (field.category === "custom") {
        return field.type === "file"
          ? ["customFiles", field.field_key]
          : ["data", "custom_fields", field.field_key];
      }
      const path = BUILT_IN_MODEL_PATHS[field.key];
      return path ? path.split(".") : null;
    },

    // Chaves do 422 que pertencem a este campo — `custom_fields.<field_key>` no
    // personalizado, a `key` (mais os apelidos dos anexos) no padrão.
    fieldErrorKeys(field) {
      if (field.category === "custom") {
        return ["custom_fields." + field.field_key];
      }
      return BUILT_IN_ERROR_KEYS[field.key] || [field.key];
    },

    // Campo em erro: 422 do backend ou regra vuelidate violada após o $touch.
    fieldError(field) {
      if (this.fieldErrorKeys(field).some((key) => this.errors[key])) return true;
      const segments = this.fieldModelSegments(field);
      if (!segments) return false;
      const validator = getByPath(this.$v, segments);
      return !!(validator && validator.$error);
    },

    // Texto exibido abaixo do campo. O 422 do backend tem precedência; sem ele,
    // as regras de FORMATO do vuelidate (e-mail e data inexistente) também
    // produzem mensagem. `required` segue só com borda vermelha + aviso do
    // rodapé, que já dizem tudo para campo vazio.
    fieldErrorMessage(field) {
      const key = this.fieldErrorKeys(field).find((k) => this.errors[k]);
      const message = key ? this.errors[key] : null;
      if (typeof message === "string" && message) return message;
      const segments = this.fieldModelSegments(field);
      const validator = segments ? getByPath(this.$v, segments) : null;
      if (!validator || !validator.$error) return "";
      if (validator.email === false) return this.$t("errors.invalidEmail");
      if (validator.validDate === false) return this.$t("errors.invalidDate");
      return "";
    },

    // Primeiro erro do 422 que não está ancorado em nenhum campo em tela.
    // Genérico de propósito: cobre chave nova do backend sem precisar listá-la.
    orphanErrorMessage() {
      const anchored = new Set();
      (this.visibleFields || []).forEach((field) =>
        this.fieldErrorKeys(field).forEach((key) => anchored.add(key))
      );
      anchored.add("holder_occupation_description");

      const orphan = Object.keys(this.errors).find((key) => !anchored.has(key));
      const message = orphan ? this.errors[orphan] : null;
      return typeof message === "string" ? message : "";
    },

    // Erro da descrição da ocupação "Outro" (campo fora de options.fields).
    occupationDescriptionError() {
      if (this.errors.holder_occupation_description) return true;
      const validator = this.$v?.data?.holder_occupation_description;
      return !!(validator && validator.$error);
    },

    // Rótulo (idioma corrente) de uma opção de um campo multi/select custom.
    multiOptionLabel(field, optionId) {
      const opt = (field.options || []).find(
        (o) => String(o.id) === String(optionId),
      );
      return opt ? this.localizedText(opt.label) : optionId;
    },

    // Opções ainda não escolhidas de um campo multi (alimenta o select de add).
    availableMultiOptions(field) {
      const selected = (this.data.custom_fields[field.field_key] || []).map(
        (v) => String(v),
      );
      return (field.options || []).filter(
        (opt) => !selected.includes(String(opt.id)),
      );
    },

    // Adiciona/remove um id no array de um campo multi (vira chip colorido).
    addMultiValue(fieldKey, value) {
      if (!value) return;
      const list = (this.data.custom_fields[fieldKey] || []).slice();
      if (!list.map((v) => String(v)).includes(String(value))) list.push(value);
      this.$set(this.data.custom_fields, fieldKey, list);
    },

    removeMultiValue(fieldKey, value) {
      const list = (this.data.custom_fields[fieldKey] || []).filter(
        (v) => String(v) !== String(value),
      );
      this.$set(this.data.custom_fields, fieldKey, list);
    },

    // Abre/fecha o dropdown custom do multi (um por vez).
    toggleMulti(fieldKey) {
      this.openMultiKey = this.openMultiKey === fieldKey ? null : fieldKey;
    },

    // Enter/Espaço no controle do multi abre e fecha o menu. Ignora a tecla que
    // veio do ✕ de um chip: ele é um <button> e já dispara o click sozinho, e sem
    // isto o evento subia e reabria o menu ao remover uma opção pelo teclado.
    onMultiKeydown(event, fieldKey) {
      const target = event.target;
      if (target && target.closest && target.closest("button")) return;
      event.preventDefault();
      this.toggleMulti(fieldKey);
    },

    // Escolher uma opção pelo teclado tira ela da lista (virou chip), então o nó
    // que tinha o foco deixa de existir e o foco cairia no body. Devolve para o
    // controle, que é de onde o titular continua navegando.
    addMultiValueByKeyboard(event, fieldKey, optionId) {
      const control = event.target.closest
        ? event.target
            .closest(".multi-select")
            ?.querySelector(".multi-select__control")
        : null;
      this.addMultiValue(fieldKey, optionId);
      if (control) this.$nextTick(() => control.focus());
    },

    // Fecha o dropdown ao clicar fora de qualquer multi-select.
    onDocCloseMulti(event) {
      if (event.target && event.target.closest(".multi-select")) return;
      this.openMultiKey = null;
    },

    // Arquivos selecionados de um campo custom type=file (upload no postForm).
    // Mesmo limite dos anexos padrão: o que passa vai para customFiles (é o que
    // sobe), o que estoura fica à mostra em invalidCustomFiles para o titular
    // remover. Antes qualquer tamanho era aceito aqui e só o S3 recusava, já com
    // o botão em "Enviando...".
    handleCustomFiles(event, fieldKey) {
      const selected = Array.from(event.target.files);
      this.$set(
        this.customFiles,
        fieldKey,
        selected.filter((file) => file.size <= this.maxFileSize),
      );
      this.$set(
        this.invalidCustomFiles,
        fieldKey,
        selected.filter((file) => file.size > this.maxFileSize),
      );
      this.refreshFileSizeError();
      event.target.value = "";
    },

    removeCustomFile(fieldKey, index) {
      const list = (this.customFiles[fieldKey] || []).slice();
      list.splice(index, 1);
      this.$set(this.customFiles, fieldKey, list);
    },

    removeInvalidCustomFile(fieldKey, index) {
      const list = (this.invalidCustomFiles[fieldKey] || []).slice();
      list.splice(index, 1);
      this.$set(this.invalidCustomFiles, fieldKey, list);
      this.refreshFileSizeError();
    },

    async submitFiles(files) {
      const response = await axios.post(
        process.env.VUE_APP_API_MS_URL + "lgpd-titular/submit-files",
        {
          files: files.map((file) => {
            let mime = lookup(file.name)
            if (!mime) {
              mime = file.name.split('.').pop();
            }
          return {
            file: file.name,
            acl: "private",
            mime: mime,
            size: file.size,
          }}),
        },
        {
          params: {
            client: this.client,
            form: this.form,
          },
        }
      );
      return response;
    },

    async postForm() {
      this.submitErrorMessage = "";
      this.showError = false;
      this.errors = {};

      // Regras geradas em `validations()` a partir dos fields visíveis. Sem o
      // $touch elas nunca eram avaliadas e o required/email de campo custom só
      // aparecia depois do 422 do backend.
      this.$v.$touch();
      if (this.$v.$invalid) {
        // A Evidência do representante legal ganha mensagem própria: "verifique
        // os erros" não diz ao titular que o que falta é o anexo. Quem decide se
        // ela é obrigatória é o `required` do campo no manifesto (a regra do
        // vuelidate acima), não mais a flag `show_applicant_field`.
        const evidenceField = this.activeFieldByKey("applicant_files");
        if (evidenceField && this.fieldError(evidenceField)) {
          this.submitErrorMessage = this.$t("errors.evidenceMissingError");
        }
        this.showError = true;
        return;
      }

      this.saving = true;
      // Máscara é por idioma (dd/mm/aaaa em pt-BR/esp, aaaa/mm/dd em en/de) —
      // a conversão para AAAA-MM-DD segue a máscara, não o idioma hardcodado.
      const typedBirthDate = (this.birth_date || "").trim();
      this.data.birth_date = typedBirthDate
        ? this.maskedDateToIso(typedBirthDate)
        : null;

      // Uploads dentro do try: uma falha aqui (submit-files ou S3) rejeitava o
      // postForm antes do axios.post, e como o `saving = false` mora no `.finally`
      // daquele POST o botão ficava preso em "Enviando..." para sempre, sem
      // mensagem nenhuma. Os uuids são remontados a cada tentativa: um envio que
      // falhava no meio deixava uuid no array e o próximo duplicava o anexo.
      try {
        // Quem manda nos anexos é o manifesto (§2.1): sobe o que foi selecionado
        // no campo que está ATIVO, em vez de consultar as flags legadas
        // `show_applicant_field`/`show_file_field` — elas ignoravam o `required` e
        // o `dependent_of` do campo. Campo ocultado já chega aqui sem seleção
        // (clear-on-hide), e o obrigatório já foi barrado pelo vuelidate acima.
        const evidenceField = this.activeFieldByKey("applicant_files");
        const filesField = this.activeFieldByKey("files");

        if (evidenceField && this.validFiles['applicantFiles']?.length) {
          const { data } = await this.submitFiles(this.validFiles['applicantFiles']);
          this.data.applicantFiles = [];
          for (const [index, item] of data?.entries()) {
            const s3Service = new S3Service(decodeURIComponent(item.data?.url));
            // Sobe da MESMA lista que gerou as URLs assinadas (validFiles): pelo
            // índice de `applicantFiles`, que guarda a seleção crua, um arquivo
            // recusado por tamanho deslocava tudo e subia o arquivo errado.
            await s3Service.upload(
              this.validFiles['applicantFiles'][index],
              item.data?.inputs,
            );
            this.data.applicantFiles.push(item.file_uuid);
          }
        }

        if (filesField && this.validFiles['defaultFiles']?.length) {
          const { data } = await this.submitFiles(this.validFiles['defaultFiles']);
          this.data.files = [];
          for (const [index, item] of data?.entries()) {
            const s3Service = new S3Service(decodeURIComponent(item.data?.url));
            await s3Service.upload(
              this.validFiles['defaultFiles'][index],
              item.data?.inputs,
            );
            this.data.files.push(item.file_uuid);
          }
        }

        // Upload dos campos custom type=file → custom_fields[field_key] = [file_uuid].
        for (const field of this.visibleFields) {
          if (field.category !== "custom" || field.type !== "file") continue;
          const selected = this.customFiles[field.field_key] || [];
          const uuids = [];
          if (selected.length) {
            const { data } = await this.submitFiles(selected);
            for (const [index, item] of data.entries()) {
              const s3Service = new S3Service(decodeURIComponent(item.data?.url));
              await s3Service.upload(selected[index], item.data?.inputs);
              uuids.push(item.file_uuid);
            }
          }
          this.$set(this.data.custom_fields, field.field_key, uuids);
        }
      } catch (error) {
        this.submitErrorMessage = this.$t("errors.uploadError");
        this.showError = true;
        this.saving = false;
        return;
      }

      axios
        .post(process.env.VUE_APP_API_MS_URL + "lgpd-titular", {
          client: this.client,
          form: this.form,
          language: this.localLanguage,
          ...this.data,
          recaptchaToken: this.recaptchaToken,
          custom_fields: this.buildCustomFieldsPayload(),
        })
        .then(() => {
          this.showForm = false;
        })
        .catch((error) => {
          if (error.response && error.response.status == 422) {
            const responseErrors = error.response.data.errors || {};
            for (let key in responseErrors) {
              // guarda a MENSAGEM, não um booleano: é ela que diz ao titular o
              // que corrigir. O backend manda um array por chave; a primeira é
              // a mais específica. Fallback para o aviso genérico se vier vazia,
              // para a borda vermelha nunca ficar sem explicação.
              const messages = responseErrors[key];
              this.$set(
                this.errors,
                key,
                (Array.isArray(messages) ? messages[0] : messages) ||
                  this.$t("errors.validationError")
              );
            }
            // Erro que não pertence a nenhum campo em tela (cap agregado de
            // custom_fields, recaptcha, form) não tem onde ancorar: reusa o
            // aviso do rodapé em vez de sumir.
            this.submitErrorMessage = this.orphanErrorMessage();
          }
          this.showError = true;
          this.recaptchaToken = null;
          if (this.$refs.recaptchaComponent) {
            this.$refs.recaptchaComponent.reset();
          }
          setTimeout(() => {
            var body = document.body,
              html = document.documentElement;

            var height = Math.max(
              body.scrollHeight,
              body.offsetHeight,
              html.clientHeight,
              html.scrollHeight,
              html.offsetHeight
            );

            window.top.postMessage(
              JSON.stringify({
                type: "height",
                height: height - 20,
              }),
              "*"
            );
          }, 100);
        })
        .finally(() => {
          this.saving = false;
        });
    },

    getLogo() {
      return process.env.VUE_APP_LOGO;
    },

        /**
     * Calculate contrast ratio between two hex colors
     * @param {string} hex1 - First hex color (e.g., "#FFFFFF").
     * @param {string} hex2 - Second hex color (e.g., "#000000").
     * @returns {number} - Contrast ratio (e.g., 21 for black and white).
     */
     getContrastRatio(hex1, hex2) {
      // Helper function to convert hex to RGB
      const hexToRgb = (hex) => {
        const value = hex.replace("#", "");
        return {
          r: parseInt(value.substring(0, 2), 16),
          g: parseInt(value.substring(2, 4), 16),
          b: parseInt(value.substring(4, 6), 16),
        };
      };

      // Helper function to calculate relative luminance
      const getLuminance = ({ r, g, b }) => {
        const toLinear = (channel) => {
          channel /= 255;
          return channel <= 0.03928
            ? channel / 12.92
            : Math.pow((channel + 0.055) / 1.055, 2.4);
        };
        return (
          0.2126 * toLinear(r) + 0.7152 * toLinear(g) + 0.0722 * toLinear(b)
        );
      };

      const rgb1 = hexToRgb(hex1);
      const rgb2 = hexToRgb(hex2);

      const lum1 = getLuminance(rgb1);
      const lum2 = getLuminance(rgb2);

      // Calculate contrast ratio
      const brighter = Math.max(lum1, lum2);
      const darker = Math.min(lum1, lum2);

      return ((brighter + 0.05) / (darker + 0.05)).toFixed(2);
    },
  },

  // Regras vuelidate geradas dos fields VISÍVEIS — padrão E personalizados. O
  // `required` é o do próprio field (vem do editor), então `message` deixa de ser
  // sempre obrigatório e qualquer campo padrão pode passar a ser. Avaliadas no
  // `postForm` ($touch + $invalid) e exibidas por `fieldError`.
  validations() {
    const rules = {};
    this.visibleFields.forEach((field) => {
      if (field.type === "support") return;
      const segments = this.fieldModelSegments(field);
      if (!segments) return;
      const fieldRules = this.fieldRules(field);
      if (Object.keys(fieldRules).length) setByPath(rules, segments, fieldRules);
    });
    // "Outro" na ocupação: a descrição é obrigatória. Regra da própria tela — o
    // campo não existe em options.fields.
    if (
      this.otherOccupationId &&
      this.data.holder_occupation === this.otherOccupationId
    ) {
      setByPath(rules, ["data", "holder_occupation_description"], { required });
    }
    return rules;
  },
};
</script>

<style src="./style.css" scoped></style>
